// Domain: KYB

import { apiGet, apiPost } from "../core/request";

const unwrap = <T>(r: any): T =>
  (r && typeof r === "object" && "data" in r ? r.data : r) as T;

export type KybDbStatus = "approved" | "pending" | "rejected";
export type KybUiStatus = KybDbStatus | "not_started";

export type KybDoc = {
  id: number | string;
  type: string;
  file_path: string | null;
  filename?: string | null;
  url?: string | null;
  status: "pending" | "approved" | "rejected";
  review_notes?: string | null;
  created_at?: string;
};

export type KybRecord = {
  id: number | string;
  company_name?: string | null;
  registration_number?: string | number | null; // allow number
  website_url?: string | null;
  business_activity?: string | null;
  incorporation_country?: string | null;
  status: KybDbStatus;
  documents?: KybDoc[];
  updated_at?: string;
};

export type BusinessDataFallback = {
  company_name?: string | null;

  // Registration number aliases (string or number)
  registration_number?: string | number | null;
  company_registration_number?: string | number | null;
  company_registration?: string | number | null;
  company_reg_number?: string | number | null;
  company_reg_no?: string | number | null;
  registration_no?: string | number | null;
  company_number?: string | number | null;
  company_no?: string | number | null;
  reg_number?: string | number | null;
  crn?: string | number | null;

  // Country aliases
  incorporation_country?: string | null;
  incorporation_country_code?: string | null;
  incorporation_country_name?: string | null;
  country?: string | null;

  address_line1?: string | null;
  city?: string | null;
  postal_code?: string | null;
};

export type KybStatusResponse = {
  kyb: KybRecord | null;
  status: KybUiStatus;
  businessData?: BusinessDataFallback | null;
  ui_step?: "documents" | "summary";
};

export type KybBootstrap = BusinessDataFallback;

// ---- Endpoints ----

export async function apiKybStatus() {
  const res = await apiGet("/xapi/kyb/status");
  return unwrap<KybStatusResponse>(res);
}
export const apiKybSummary = apiKybStatus;

export async function apiKybBootstrap() {
  const res = await apiGet("/xapi/kyb/bootstrap");
  return unwrap<KybBootstrap>(res);
}

export async function apiKybShow(id: number | string) {
  const res = await apiGet(`/xapi/kyb/${id}`);
  return unwrap<KybRecord>(res);
}

export async function apiKybStore(body: FormData | Record<string, any>) {
  const res = await apiPost("/xapi/kyb", body);
  return unwrap<{ success: boolean; kyb_id?: number | string }>(res);
}

export async function apiKybUpdate(id: number | string, body: Record<string, any>) {
  const fd = new FormData();
  Object.entries(body).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, String(v));
  });
  fd.append("_method", "PATCH");
  const res = await apiPost(`/xapi/kyb/${id}`, fd);
  return unwrap<{ success: boolean; kyb_id: number | string }>(res);
}

export async function apiKybDocumentsUpload(form: FormData) {
  const res = await apiPost("/xapi/kyb/documents", form);
  return unwrap<{
    success: boolean;
    created: Array<{ id: number | string; type: string; url: string; status: string }>;
  }>(res);
}

export async function apiKybDocumentReupload(id: number | string, file: File) {
  const fd = new FormData();
  fd.set("document", file);
  const res = await apiPost(`/xapi/kyb/document/${id}/reupload`, fd);
  return unwrap<{ success: boolean; id: number | string; url: string; status: string }>(res);
}
